<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title>
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark>mdi-arrow-left</v-icon>
          </v-btn>
          Add a Prescription Item
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col sm="6">
              <v-autocomplete
                v-model="form.prescription_id"
                :items="prescriptions"
                :item-text="getPrescriptionDisplayText"
                item-value="id"
                solo
                rounded
                label="Select Prescription"
                prepend-inner-icon="mdi-file-document"
                :error-messages="errors.prescription_id"
                clearable
                required
              ></v-autocomplete>
            </v-col>
            <v-col sm="6">
              <v-autocomplete
                v-model="form.product_id"
                :items="products"
                :item-text="getProductDisplayText"
                item-value="id"
                solo
                rounded
                label="Select Product"
                prepend-inner-icon="mdi-pill"
                :error-messages="errors.product_id"
                clearable
                required
              ></v-autocomplete>
            </v-col>
            <v-col sm="6">
              <v-autocomplete
                v-model="form.mode_id"
                :items="modes"
                item-text="name"
                item-value="id"
                solo
                rounded
                label="Administration Mode (Optional)"
                prepend-inner-icon="mdi-medical-bag"
                :error-messages="errors.mode_id"
                clearable
              ></v-autocomplete>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.dosage"
                solo
                rounded
                label="Dosage (mg)"
                prepend-inner-icon="mdi-weight"
                type="number"
                min="0"
                step="0.01"
                :error-messages="errors.dosage"
                placeholder="e.g., 500"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-autocomplete
                v-model="form.frequency"
                :items="frequencyOptions"
                solo
                rounded
                label="Frequency"
                prepend-inner-icon="mdi-clock-outline"
                :error-messages="errors.frequency"
                clearable
              ></v-autocomplete>
            </v-col>
            <v-col sm="6">
              <v-autocomplete
                v-model="form.duration"
                :items="durationOptions"
                solo
                rounded
                label="Duration"
                prepend-inner-icon="mdi-calendar-range"
                :error-messages="errors.duration"
                clearable
              ></v-autocomplete>
            </v-col>
            <v-col sm="6">
              <v-switch
                class="mt-0 ml-5"
                v-model="form.is_active"
                inset
                :label="`Is Active: ${form.is_active ? 'YES' : 'NO'}`"
              ></v-switch>
            </v-col>
            <v-col sm="12">
              <v-textarea
                v-model="form.notes"
                solo
                rounded
                label="Instructions/Notes"
                rows="4"
                prepend-inner-icon="mdi-note-text"
                :error-messages="errors.notes"
                placeholder="Enter special instructions, warnings, or additional notes..."
              ></v-textarea>
            </v-col>
          </v-row>
          <v-divider class="mt-5 mb-5"></v-divider>
          <v-row>
            <v-col sm="12">
              <v-btn
                :loading="isSaving"
                :disabled="isSaving"
                color="primary"
                @click="savePrescriptionItem"
                class="mr-4"
              >
                <v-icon left>mdi-content-save</v-icon>
                Save Prescription Item
              </v-btn>
              <v-btn
                color="secondary"
                @click="$router.back()"
                :disabled="isSaving"
              >
                <v-icon left>mdi-cancel</v-icon>
                Cancel
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Success Dialog -->
      <v-dialog v-model="showSuccessDialog" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-check-circle
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Prescription Item created successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="
                          showSuccessDialog = false;
                          $router.push('/prescription-items');
                        "
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "PrescriptionItemCreate",
  data() {
    return {
      isSaving: false,
      showSuccessDialog: false,
      prescriptions: [],
      products: [],
      modes: [],
      errors: {},

      form: {
        prescription_id: null,
        product_id: null,
        mode_id: null,
        dosage: null,
        frequency: "",
        duration: "",
        notes: "",
        is_active: true,
      },

      frequencyOptions: [
        "once daily",
        "twice daily",
        "three times daily",
        "four times daily",
        "every 6 hours",
        "every 8 hours",
        "every 12 hours",
        "as needed",
        "before meals",
        "after meals",
        "at bedtime",
        "once weekly",
        "twice weekly",
      ],

      durationOptions: [
        "3 days",
        "5 days",
        "7 days",
        "10 days",
        "14 days",
        "21 days",
        "30 days",
        "6 weeks",
        "8 weeks",
        "3 months",
        "as directed",
        "until finished",
      ],
    };
  },

  mounted() {
    this.getMasters();
  },

  methods: {
    async getMasters() {
      try {
        const response = await axios.get("masters?context=prescription_items");

        if (response.data?.prescriptions) {
          this.prescriptions = response.data.prescriptions;
        }

        if (response.data?.products) {
          this.products = response.data.products;
        }

        if (response.data?.modes) {
          this.modes = response.data.modes.map(mode => ({
            id: mode.id,
            name: mode.description,
            value: mode.id,
            text: mode.description
          }));
        }
      } catch (error) {
        console.error("Error fetching masters:", error);
      }
    },

    getPrescriptionDisplayText(prescription) {
      if (prescription.doctor) {
        return `Prescription #${prescription.id} - Dr. ${prescription.doctor.user_name}`;
      }
      return `Prescription #${prescription.id}`;
    },

    getProductDisplayText(product) {
      if (product.category) {
        return `${product.name}`;
        // return `${product.name} (${product.category})`;
      }
      return product.name;
    },

    async savePrescriptionItem() {
      this.isSaving = true;
      this.errors = {};

      try {
        // Prepare the data
        const prescriptionItemData = {
          prescription_id: this.form.prescription_id,
          product_id: this.form.product_id,
          mode_id: this.form.mode_id,
          dosage: this.form.dosage,
          frequency: this.form.frequency,
          duration: this.form.duration,
          notes: this.form.notes,
          is_active: this.form.is_active,
        };

        const response = await axios.post(
          "/prescription_items",
          prescriptionItemData
        );

        if (response.data.success) {
          this.showSuccessDialog = true;
          this.resetForm();
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              response.data.message ||
              "An error occurred while creating the prescription item.",
          });
        }
      } catch (error) {
        console.error(error);

        if (error.response) {
          if (error.response.status === 422) {
            // Validation errors
            this.errors = error.response.data.errors || {};
          } else if (error.response.status === 401) {
            // Authentication error
            this.$store.dispatch("errors/setError", {
              message: "Authentication required. Please login again.",
            });
            this.$router.push("/login");
          } else {
            // Other errors
            this.$store.dispatch("errors/setError", {
              message:
                error.response.data.message ||
                "An error occurred while creating the prescription item.",
            });
          }
        } else {
          // Network or other errors
          this.$store.dispatch("errors/setError", {
            message:
              "Network error. Please check your connection and try again.",
          });
        }
      } finally {
        this.isSaving = false;
      }
    },

    resetForm() {
      this.form = {
        prescription_id: null,
        product_id: null,
        mode_id: null,
        dosage: null,
        frequency: "",
        duration: "",
        notes: "",
        is_active: true,
      };
      this.errors = {};
    },
  },
};
</script>

<style scoped>
.v-card-title {
  font-weight: bold;
  font-size: 1.5rem;
}

.v-text-field.solo .v-input__control {
  min-height: 48px;
}

.v-textarea.solo .v-input__control {
  min-height: 120px;
}
</style>